import React, { useEffect, useState, useMemo } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { PageHeader } from "@/components/Layout";
import { Trophy, Target, CheckCircle2, XCircle, Clock, CreditCard, Bell, LogOut, Calendar, Award, Users, Copy, Gift, ListChecks, ChevronDown } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { fmtDateTime, fmtDay, euro } from "@/lib/format";
import { useApp } from "@/context/AppContext";
import { toast } from "sonner";

function Stat({ icon: Icon, label, value, color = "text-gold-300" }) {
  return (
    <div className="surface p-4">
      <Icon size={20} className={color} />
      <div className="font-heading text-3xl font-black text-white mt-2">{value}</div>
      <div className="text-xs text-muted-foreground uppercase tracking-wide">{label}</div>
    </div>
  );
}

export default function Profile() {
  const { user, subscription, hasActiveSub, loadSubscription, notifications, loadNotifications } = useApp();
  const [params, setParams] = useSearchParams();
  const [rank, setRank] = useState(null);
  const [preds, setPreds] = useState([]);
  const [payments, setPayments] = useState([]);
  const [referral, setReferral] = useState(null);

  const loadAll = async () => {
    const [r, p, s, ref] = await Promise.all([
      api.get("/leaderboard/me").then((x) => x.data).catch(() => null),
      api.get("/predictions/mine").then((x) => x.data).catch(() => []),
      api.get("/subscription/mine").then((x) => x.data).catch(() => ({ payments: [] })),
      api.get("/referral/me").then((x) => x.data).catch(() => null),
    ]);
    setRank(r); setPreds(p); setPayments(s.payments || []); setReferral(ref);
  };

  useEffect(() => { loadAll(); loadNotifications(); /* eslint-disable-next-line */ }, []);

  useEffect(() => {
    if (params.get("subscription") === "success") {
      const sid = params.get("subscription_id");
      (async () => {
        try {
          // find the pending subscription to verify
          const { data } = await api.get("/subscription/mine");
          const psid = sid || data.subscription?.provider_subscription_id;
          if (psid) {
            await api.post("/subscription/verify", { subscription_id: psid });
            toast.success("Abbonamento attivato!");
            await loadSubscription();
            await loadAll();
          }
        } catch (e) { toast.error(apiErr(e)); }
        setParams({});
      })();
    }
  }, [params]);

  const cancelSub = async () => {
    try { await api.post("/subscription/cancel"); toast.success("Rinnovo annullato."); loadSubscription(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const markRead = async () => { await api.post("/notifications/read"); loadNotifications(); };

  const period = rank?.period;
  const finished = preds.filter((p) => p.match.status === "finished");
  const pending = preds.filter((p) => !p.match.locked);
  const correct = finished.filter((p) => p.choice === p.match.result).length;
  const wrong = finished.length - correct;

  const [openSlip, setOpenSlip] = useState(null);
  const slips = useMemo(() => {
    const groups = {};
    preds.forEach((p) => {
      const m = p.match;
      const comp = m.competition_code === "UCL" ? "Champions League" : "Serie A";
      const key = m.matchday ? `${comp}__G${m.matchday}` : `${comp}__${fmtDay(m.kickoff)}`;
      const g = groups[key] || (groups[key] = {
        key, title: comp + (m.matchday ? ` · Giornata ${m.matchday}` : ` · ${fmtDay(m.kickoff)}`),
        items: [], date: m.kickoff, points: 0, correct: 0, resolved: 0,
      });
      g.items.push(p);
      if (new Date(m.kickoff) > new Date(g.date)) g.date = m.kickoff;
      if (m.status === "finished") {
        g.resolved++;
        if (p.choice === m.result) { g.points += 3; g.correct++; }
      }
    });
    return Object.values(groups).sort((a, b) => new Date(b.date) - new Date(a.date));
  }, [preds]);

  return (
    <div>
      <PageHeader overline={`Ciao, ${user?.name?.split(" ")[0] || ""}`} title={user?.nickname || "Profilo"}
        subtitle={hasActiveSub ? "Abbonamento attivo · pronostica e scala la classifica!" : "Abbonamento non attivo"}>
        {!hasActiveSub && <Link to="/abbonamento" className="btn-gold mt-5 inline-flex">Attiva abbonamento</Link>}
      </PageHeader>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10 grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <Stat icon={Trophy} label="Punti periodo" value={period?.points ?? 0} />
            <Stat icon={Award} label="Posizione" value={period?.position ? `${period.position}°` : "—"} />
            <Stat icon={CheckCircle2} label="Corretti" value={correct} color="text-status-correct" />
            <Stat icon={Clock} label="In attesa" value={pending.length} color="text-status-locked" />
          </div>

          <div className="surface p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-xl font-bold uppercase text-white">I tuoi pronostici</h3>
              <Link to="/calendario" className="text-gold-300 text-sm hover:underline">+ Nuovo</Link>
            </div>
            {preds.length === 0 ? (
              <p className="text-muted-foreground text-sm py-6 text-center">Non hai ancora inviato pronostici. <Link to="/calendario" className="text-gold-300">Inizia ora →</Link></p>
            ) : (
              <div className="space-y-2">
                {preds.slice(0, 12).map((p) => {
                  const m = p.match;
                  const done = m.status === "finished";
                  const ok = done && p.choice === m.result;
                  return (
                    <div key={p.id} className="flex items-center justify-between bg-ink-900 rounded-lg px-3 py-2.5 text-sm">
                      <div className="flex-1 min-w-0">
                        <div className="text-white truncate">{m.home_team} <span className="text-muted-foreground">vs</span> {m.away_team}</div>
                        <div className="text-xs text-muted-foreground">{fmtDateTime(m.kickoff)} · {m.competition_code === "UCL" ? "UCL" : "Serie A"}</div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="font-heading font-bold text-lg text-gold-300 w-6 text-center">{p.choice}</span>
                        {done ? (
                          ok ? <span className="text-status-correct flex items-center gap-1 text-xs"><CheckCircle2 size={14} />+3</span>
                             : <span className="text-destructive flex items-center gap-1 text-xs"><XCircle size={14} />0</span>
                        ) : m.locked ? <Clock size={14} className="text-status-locked" /> : <span className="text-xs text-slate-500">modificabile</span>}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="surface p-5" data-testid="my-slips">
            <div className="flex items-center gap-2 mb-4">
              <ListChecks size={20} className="text-gold-300" />
              <h3 className="font-heading text-xl font-bold uppercase text-white">Le mie schedine</h3>
              <span className="ml-auto text-sm text-muted-foreground">{slips.length} schedine</span>
            </div>
            {slips.length === 0 ? (
              <p className="text-muted-foreground text-sm py-6 text-center">Nessuna schedina inviata. <Link to="/calendario" className="text-gold-300">Compila la tua prima schedina →</Link></p>
            ) : (
              <div className="space-y-2">
                {slips.map((s) => {
                  const open = openSlip === s.key;
                  return (
                    <div key={s.key} className="bg-ink-900 rounded-lg overflow-hidden">
                      <button onClick={() => setOpenSlip(open ? null : s.key)} data-testid={`slip-${s.key}`}
                        className="w-full flex items-center justify-between px-4 py-3 text-left">
                        <div className="min-w-0">
                          <div className="font-semibold text-white truncate">{s.title}</div>
                          <div className="text-xs text-muted-foreground">{fmtDay(s.date)} · {s.items.length} pronostici · {s.correct}/{s.resolved || 0} corretti</div>
                        </div>
                        <div className="flex items-center gap-3 shrink-0">
                          <span className="font-heading font-black text-lg text-gold-300">{s.points} pt</span>
                          <ChevronDown size={18} className={`text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`} />
                        </div>
                      </button>
                      {open && (
                        <div className="px-4 pb-3 space-y-1.5 border-t border-gold-300/10 pt-3">
                          {s.items.slice().sort((a, b) => new Date(a.match.kickoff) - new Date(b.match.kickoff)).map((p) => {
                            const m = p.match;
                            const done = m.status === "finished";
                            const ok = done && p.choice === m.result;
                            return (
                              <div key={p.id} className="flex items-center justify-between text-sm">
                                <div className="flex-1 min-w-0">
                                  <span className="text-white truncate">{m.home_team} <span className="text-muted-foreground">vs</span> {m.away_team}</span>
                                  {done && <span className="text-xs text-muted-foreground ml-1">({m.home_score}-{m.away_score})</span>}
                                </div>
                                <div className="flex items-center gap-2 shrink-0">
                                  <span className="font-heading font-bold text-gold-300 w-5 text-center">{p.choice}</span>
                                  {done
                                    ? (ok ? <CheckCircle2 size={18} className="text-status-correct" data-testid={`slip-result-correct-${p.id}`} />
                                          : <XCircle size={18} className="text-destructive" data-testid={`slip-result-wrong-${p.id}`} />)
                                    : <Clock size={16} className="text-status-locked" title="In attesa del risultato" />}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <div className="space-y-6">
          <div className="surface p-5">
            <h3 className="font-heading text-lg font-bold uppercase text-white flex items-center gap-2 mb-3"><CreditCard size={18} className="text-gold-300" /> Abbonamento</h3>
            {subscription ? (
              <div className="space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-muted-foreground">Stato</span>
                  <span className={subscription.status === "active" ? "text-status-correct font-semibold" : "text-status-locked font-semibold"}>
                    {subscription.status === "active" ? "Attivo" : subscription.status === "past_due" ? "Pagamento in sospeso" : subscription.status === "pending" ? "In attesa" : "Annullato"}
                  </span>
                </div>
                <div className="flex justify-between"><span className="text-muted-foreground">Prezzo</span><span className="text-white">{euro(subscription.price, subscription.currency)}/mese</span></div>
                {subscription.next_billing_date && <div className="flex justify-between"><span className="text-muted-foreground">Prossimo rinnovo</span><span className="text-white">{fmtDay(subscription.next_billing_date)}</span></div>}
                {subscription.status === "active" && !subscription.cancel_at_period_end && (
                  <button onClick={cancelSub} data-testid="cancel-sub-btn" className="btn-outline-gold w-full mt-3 text-sm !py-2">Annulla rinnovo</button>
                )}
                {subscription.cancel_at_period_end && <p className="text-xs text-status-locked mt-2">Rinnovo annullato: l'accesso resta fino alla scadenza.</p>}
              </div>
            ) : (
              <div>
                <p className="text-sm text-muted-foreground mb-3">Nessun abbonamento attivo.</p>
                <Link to="/abbonamento" className="btn-gold w-full text-sm !py-2">Abbonati · 4,99€</Link>
              </div>
            )}
          </div>

          <div className="surface p-5">
            <h3 className="font-heading text-lg font-bold uppercase text-white flex items-center gap-2 mb-3"><Users size={18} className="text-gold-300" /> Porta 3 amici</h3>
            {referral ? (
              <>
                {referral.is_free && (
                  <div className="flex items-center gap-2 bg-status-correct/10 border border-status-correct/30 rounded-lg p-2.5 mb-3 text-sm text-status-correct">
                    <Gift size={16} /> Abbonamento gratuito attivo!
                  </div>
                )}
                <div className="flex items-end justify-between mb-1">
                  <span className="text-sm text-muted-foreground">Referral attivi</span>
                  <span className="font-heading text-2xl font-black text-gold-300" data-testid="referral-count">{referral.active_referrals}<span className="text-base text-muted-foreground">/{referral.threshold}</span></span>
                </div>
                <div className="h-2 bg-ink-900 rounded-full overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-gold-200 to-gold-500 transition-all" style={{ width: `${Math.min(100, (referral.active_referrals / referral.threshold) * 100)}%` }} />
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  {referral.is_free ? "Mantieni almeno 3 referral attivi per restare gratis."
                    : `Ti mancano ${referral.needed} referral per l'abbonamento gratuito.`}
                </p>
                <div className="mt-4">
                  <span className="text-xs text-muted-foreground uppercase tracking-wide">Il tuo codice sponsor</span>
                  <div className="flex items-center gap-2 mt-1">
                    <code className="font-mono-ui text-gold-300 bg-ink-900 rounded px-3 py-2 flex-1 text-sm">{referral.code}</code>
                    <button onClick={() => { navigator.clipboard?.writeText(referral.code); toast.success("Codice copiato!"); }} className="btn-outline-gold !p-2.5" data-testid="referral-copy" aria-label="Copia codice"><Copy size={16} /></button>
                  </div>
                </div>
                {referral.sponsor && <p className="text-xs text-muted-foreground mt-3">Il tuo sponsor: <span className="text-slate-200">{referral.sponsor}</span></p>}
                {referral.referrals.length > 0 && (
                  <div className="mt-3 space-y-1 max-h-40 overflow-auto">
                    {referral.referrals.map((rf, i) => (
                      <div key={i} className="flex items-center justify-between text-sm bg-ink-900 rounded px-2.5 py-1.5">
                        <span className="text-slate-200">{rf.nickname}</span>
                        <span className={`text-xs ${rf.active ? "text-status-correct" : "text-muted-foreground"}`}>{rf.active ? "attivo" : "in attesa"}</span>
                      </div>
                    ))}
                  </div>
                )}
              </>
            ) : <p className="text-sm text-muted-foreground">Caricamento…</p>}
          </div>

          <div className="surface p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-heading text-lg font-bold uppercase text-white flex items-center gap-2"><Bell size={18} className="text-gold-300" /> Notifiche</h3>
              {notifications.some((n) => !n.read) && <button onClick={markRead} className="text-xs text-gold-300 hover:underline">Segna lette</button>}
            </div>
            {notifications.length === 0 ? (
              <p className="text-sm text-muted-foreground">Nessuna notifica.</p>
            ) : (
              <div className="space-y-2 max-h-72 overflow-auto">
                {notifications.slice(0, 15).map((n) => (
                  <div key={n.id} className={`rounded-lg px-3 py-2 text-sm ${n.read ? "bg-ink-900" : "bg-gold-300/10 border border-gold-300/20"}`}>
                    <div className="font-semibold text-white">{n.title}</div>
                    <div className="text-xs text-muted-foreground">{n.body}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
